import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
  MessageSquare, Plus, Trash2, Save, Loader2, RefreshCw,
  GripVertical, Clock, Sparkles, Briefcase, Users, Edit2, Code2,
  Wand2, CheckCircle2, XCircle, Zap, ChevronDown, ChevronUp,
  Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { suggestCodingQuestions } from '../services/api';

const CategoryBadge = ({ category }) => {
  const colors = {
    technical: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    work_setup: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    culture: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    coding: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  };
  
  const labels = {
    technical: 'Technical',
    work_setup: 'Work Setup',
    culture: 'Culture Fit',
    coding: 'Coding',
  };
  
  return (
    <Badge className={`${colors[category] || colors.technical} text-xs`}>
      {labels[category] || category}
    </Badge>
  );
};

const DifficultyBadge = ({ difficulty }) => {
  const colors = {
    easy: 'bg-green-500/10 text-green-600 border-green-500/20',
    medium: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    hard: 'bg-red-500/10 text-red-600 border-red-500/20',
  };
  return (
    <Badge className={`${colors[difficulty] || colors.medium} text-xs`}>
      {difficulty?.charAt(0).toUpperCase() + difficulty?.slice(1)}
    </Badge>
  );
};

const QuestionItem = ({ question, index, onUpdate, onDelete, isEditing, onEditToggle }) => {
  const [localQuestion, setLocalQuestion] = useState(question);
  
  useEffect(() => {
    setLocalQuestion(question);
  }, [question]);

  const handleSave = () => {
    onUpdate(index, localQuestion);
    onEditToggle(null);
  };

  if (isEditing) {
    return (
      <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground w-8">Q{index + 1}</span>
          <Select 
            value={localQuestion.category} 
            onValueChange={(val) => setLocalQuestion({ ...localQuestion, category: val })}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="technical">Technical</SelectItem>
              <SelectItem value="work_setup">Work Setup</SelectItem>
              <SelectItem value="culture">Culture Fit</SelectItem>
              <SelectItem value="coding">Coding</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 ml-auto">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              value={localQuestion.time_limit}
              onChange={(e) => setLocalQuestion({ ...localQuestion, time_limit: parseInt(e.target.value) || 120 })}
              className="w-20 h-8"
            />
            <span className="text-xs text-muted-foreground">sec</span>
          </div>
        </div>
        <Textarea
          value={localQuestion.question}
          onChange={(e) => setLocalQuestion({ ...localQuestion, question: e.target.value })}
          className="min-h-[80px]"
          placeholder="Enter your question..."
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEditToggle(null)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            <Save className="h-3.5 w-3.5 mr-1" />
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group p-3 border rounded-lg hover:border-primary/30 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 cursor-grab" />
          <span className="text-sm font-bold text-muted-foreground w-6">Q{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-relaxed">{question.question}</p>
          <div className="flex items-center gap-2 mt-2">
            <CategoryBadge category={question.category} />
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> {question.time_limit}s
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditToggle(index)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(index)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ===== Coding Suggestion Card ===== */
const CodingSuggestionCard = ({ suggestion, onAdd, isAdded }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border rounded-lg transition-all ${isAdded ? 'border-emerald-500/40 bg-emerald-500/5' : 'hover:border-primary/30'}`}>
      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Code2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span className="text-sm font-semibold truncate">{suggestion.title}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <DifficultyBadge difficulty={suggestion.difficulty} />
              <Badge variant="outline" className="text-xs gap-1">
                <Clock className="h-3 w-3" />{suggestion.time_limit || 300}s
              </Badge>
              {suggestion.relevant_skills?.map((skill, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            {isAdded ? (
              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1">
                <CheckCircle2 className="h-3 w-3" /> Added
              </Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                onClick={() => onAdd(suggestion)}
                data-testid={`add-suggestion-${suggestion.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add
              </Button>
            )}
          </div>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{suggestion.question}</p>
            {suggestion.recommended_languages?.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground">Languages:</span>
                {suggestion.recommended_languages.map((lang, i) => (
                  <Badge key={i} variant="outline" className="text-xs capitalize">{lang}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const InterviewQuestionsEditor = ({ 
  jobId, 
  questions, 
  onQuestionsUpdate, 
  onGenerate,
  isGenerating,
  isSaving,
  showResultsToCandidate,
  onToggleShowResults
}) => {
  const [localQuestions, setLocalQuestions] = useState(questions || []);
  const [editingIndex, setEditingIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Coding suggestion state
  const [showCodingSuggestions, setShowCodingSuggestions] = useState(false);
  const [codingSuggestions, setCodingSuggestions] = useState(null);
  const [isSuggestingCoding, setIsSuggestingCoding] = useState(false);
  const [addedSuggestionIds, setAddedSuggestionIds] = useState(new Set());

  useEffect(() => {
    setLocalQuestions(questions || []);
    setHasChanges(false);
  }, [questions]);

  const handleUpdate = (index, updatedQuestion) => {
    const newQuestions = [...localQuestions];
    newQuestions[index] = updatedQuestion;
    setLocalQuestions(newQuestions);
    setHasChanges(true);
  };

  const handleDelete = (index) => {
    const newQuestions = localQuestions.filter((_, i) => i !== index);
    newQuestions.forEach((q, i) => { q.order = i + 1; });
    setLocalQuestions(newQuestions);
    setHasChanges(true);
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `new-${Date.now()}`,
      question: '',
      category: 'technical',
      order: localQuestions.length + 1,
      time_limit: 120
    };
    setLocalQuestions([...localQuestions, newQuestion]);
    setEditingIndex(localQuestions.length);
    setHasChanges(true);
  };

  const handleSaveAll = () => {
    onQuestionsUpdate(localQuestions);
  };

  // ===== AI Coding Suggestion Functions =====
  const handleSuggestCoding = async () => {
    setIsSuggestingCoding(true);
    setCodingSuggestions(null);
    setShowCodingSuggestions(true);
    setAddedSuggestionIds(new Set());
    try {
      const result = await suggestCodingQuestions(jobId);
      setCodingSuggestions(result);
      if (!result.requires_coding) {
        toast.info(result.reason || 'This role does not require coding assessments.');
      }
    } catch (err) {
      toast.error('Failed to generate coding suggestions');
      setCodingSuggestions({ requires_coding: false, reason: 'Error generating suggestions', suggestions: [] });
    } finally {
      setIsSuggestingCoding(false);
    }
  };

  const handleAddSuggestion = (suggestion) => {
    const newQuestion = {
      id: suggestion.id || `coding-${Date.now()}`,
      question: suggestion.question,
      category: 'coding',
      order: localQuestions.length + 1,
      time_limit: suggestion.time_limit || 300
    };
    setLocalQuestions(prev => [...prev, newQuestion]);
    setAddedSuggestionIds(prev => new Set([...prev, suggestion.id]));
    setHasChanges(true);
    toast.success(`Added: ${suggestion.title}`);
  };

  const handleAddAllSuggestions = () => {
    const toAdd = codingSuggestions.suggestions.filter(s => !addedSuggestionIds.has(s.id));
    if (toAdd.length === 0) return;
    const newQuestions = toAdd.map((s, i) => ({
      id: s.id || `coding-${Date.now()}-${i}`,
      question: s.question,
      category: 'coding',
      order: localQuestions.length + 1 + i,
      time_limit: s.time_limit || 300
    }));
    setLocalQuestions(prev => [...prev, ...newQuestions]);
    setAddedSuggestionIds(prev => new Set([...prev, ...toAdd.map(s => s.id)]));
    setHasChanges(true);
    toast.success(`Added ${toAdd.length} coding question${toAdd.length > 1 ? 's' : ''}`);
  };

  const techCount = localQuestions.filter(q => q.category === 'technical').length;
  const workCount = localQuestions.filter(q => q.category === 'work_setup').length;
  const cultureCount = localQuestions.filter(q => q.category === 'culture').length;
  const codingCount = localQuestions.filter(q => q.category === 'coding').length;

  return (
    <Card data-testid="interview-questions-editor">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
            AI Interview Questions
          </CardTitle>
          <div className="flex items-center gap-2">
            {localQuestions.length === 0 ? (
              <Button onClick={onGenerate} disabled={isGenerating} data-testid="generate-questions-btn">
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={onGenerate} disabled={isGenerating}>
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                {hasChanges && (
                  <Button size="sm" onClick={handleSaveAll} disabled={isSaving} data-testid="save-questions-btn">
                    {isSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    ) : (
                      <Save className="h-3.5 w-3.5 mr-1" />
                    )}
                    Save Changes
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Question stats */}
        {localQuestions.length > 0 && (
          <div className="flex items-center gap-3 mt-2">
            <Badge variant="secondary" className="text-xs gap-1">
              <Briefcase className="h-3 w-3" /> {techCount} Technical
            </Badge>
            <Badge variant="secondary" className="text-xs gap-1">
              <Clock className="h-3 w-3" /> {workCount} Work Setup
            </Badge>
            <Badge variant="secondary" className="text-xs gap-1">
              <Users className="h-3 w-3" /> {cultureCount} Culture
            </Badge>
            {codingCount > 0 && (
              <Badge variant="secondary" className="text-xs gap-1 text-emerald-600">
                <Code2 className="h-3 w-3" /> {codingCount} Coding
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      
      {/* Show Results Toggle */}
      <div className="px-6 pb-3">
        <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/20" data-testid="show-results-toggle-container">
          <div className="flex items-center gap-3">
            {showResultsToCandidate ? (
              <Eye className="h-4 w-4 text-green-500" />
            ) : (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium">Show Results to Candidates</p>
              <p className="text-xs text-muted-foreground">
                {showResultsToCandidate
                  ? 'Candidates will see their scores and feedback after the interview'
                  : 'Candidates will see a Thank You page — results visible only to recruiters'}
              </p>
            </div>
          </div>
          <button
            onClick={onToggleShowResults}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${showResultsToCandidate ? 'bg-green-500' : 'bg-muted-foreground/30'}`}
            data-testid="show-results-toggle"
            role="switch"
            aria-checked={showResultsToCandidate}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${showResultsToCandidate ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>
      </div>

      <CardContent>
        {localQuestions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            <p className="text-sm">No interview questions yet</p>
            <p className="text-xs mt-1">Generate AI-powered questions based on the job description</p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[400px] pr-2">
              <div className="space-y-2">
                {localQuestions.map((question, idx) => (
                  <QuestionItem
                    key={question.id}
                    question={question}
                    index={idx}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    isEditing={editingIndex === idx}
                    onEditToggle={setEditingIndex}
                  />
                ))}
              </div>
            </ScrollArea>
            
            {/* Action buttons */}
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleAddQuestion}
                data-testid="add-question-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Question
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10" 
                onClick={handleSuggestCoding}
                disabled={isSuggestingCoding}
                data-testid="suggest-coding-btn"
              >
                {isSuggestingCoding ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing Role...</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" />AI Suggest Coding Questions</>
                )}
              </Button>
            </div>

            {/* ===== AI Coding Suggestions Panel ===== */}
            {showCodingSuggestions && (
              <div className="mt-4 border rounded-lg overflow-hidden" data-testid="coding-suggestions-panel">
                <div className="px-4 py-3 bg-emerald-500/5 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wand2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-semibold">AI Coding Suggestions</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setShowCodingSuggestions(false)}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Close
                  </Button>
                </div>

                <div className="p-4">
                  {isSuggestingCoding ? (
                    <div className="space-y-3 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                        <span className="text-sm text-muted-foreground">AI analyzing the job description for coding relevance...</span>
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-16 w-full" />
                      </div>
                    </div>
                  ) : codingSuggestions && !codingSuggestions.requires_coding ? (
                    /* Role doesn't need coding */
                    <div className="text-center py-6" data-testid="no-coding-needed">
                      <XCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                      <p className="text-sm font-medium">No Coding Assessment Needed</p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                        {codingSuggestions.reason}
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        You can still manually add coding questions using the "Add Question" button and selecting the "Coding" category.
                      </p>
                    </div>
                  ) : codingSuggestions?.suggestions?.length > 0 ? (
                    /* Show suggestions */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          <Zap className="h-3 w-3 inline mr-1 text-emerald-500" />
                          {codingSuggestions.reason}
                        </p>
                        {codingSuggestions.suggestions.filter(s => !addedSuggestionIds.has(s.id)).length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10 text-xs"
                            onClick={handleAddAllSuggestions}
                            data-testid="add-all-suggestions-btn"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Add All
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {codingSuggestions.suggestions.map((suggestion) => (
                          <CodingSuggestionCard
                            key={suggestion.id}
                            suggestion={suggestion}
                            onAdd={handleAddSuggestion}
                            isAdded={addedSuggestionIds.has(suggestion.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No suggestions available.</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InterviewQuestionsEditor;
